<template>
  <div class="compare-table">
    <div class="table-responsive">
      <table class="table table-hover">
        <caption class="visually-hidden">
          Projecten. Vink een project aan om het aan de vergelijking toe te voegen;
          er kunnen er maximaal {{ maxSelect }} tegelijk vergeleken worden.
        </caption>
        <thead class="table-light">
          <tr>
            <th scope="col" style="width: 50px;">
              <input
                type="checkbox"
                class="form-check-input"
                :checked="allSelected"
                @change="selectAll"
                :disabled="records.length === 0"
                aria-label="Alle zichtbare projecten selecteren"
              >
            </th>
            <th scope="col">#</th>
            <th scope="col" v-for="field in displayFields" :key="field">{{ getFieldLabel(field) }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(record, index) in records"
            :key="record.id"
            class="selectable-row"
            :class="{ 'selected': isSelected(record.id), 'disabled': isDisabled(record.id) }"
            @click="toggleRow(record.id)"
          >
            <td>
              <input
                type="checkbox"
                class="form-check-input"
                :checked="isSelected(record.id)"
                :disabled="isDisabled(record.id)"
                @click.stop="toggleRow(record.id)"
                :aria-label="'Vergelijk ' + recordNaam(record)"
              >
            </td>
            <td>{{ index + 1 }}</td>
            <td v-for="field in displayFields" :key="field">
              {{ formatValue(record[field]) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="records.length === 0" class="empty-state">
      <i class="fas fa-inbox" aria-hidden="true"></i>
      <p>Geen records gevonden</p>
    </div>

    <div v-if="selectedRecords.length > 0" class="selection-info" aria-live="polite">
      {{ selectedRecords.length }} van {{ maxSelect }} geselecteerd voor vergelijking
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

export default defineComponent({
  name: 'CompareTable',
  props: {
    records: {
      type: Array as PropType<any[]>,
      required: true
    },
    displayFields: {
      type: Array as PropType<string[]>,
      required: true
    },
    selectedRecords: {
      type: Array as PropType<string[]>,
      required: true
    },
    maxSelect: {
      type: Number,
      default: 20
    },
    fieldLabels: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({})
    }
  },
  emits: ['select'],
  computed: {
    allSelected(): boolean {
      if (this.records.length === 0) return false;
      const selectableRecords = this.records.slice(0, this.maxSelect);
      return selectableRecords.every(r => this.selectedRecords.includes(r.id));
    }
  },
  methods: {
    /**
     * Naam waaraan een gebruiker het project herkent, voor de aria-label van de
     * selectievakjes. Zonder dit hoort een schermlezer 72 keer "selectievakje"
     * zonder te weten waarbij het hoort.
     */
    recordNaam(record: any): string {
      const eerste = this.displayFields[0];
      const waarde = eerste ? record[eerste] : '';
      return waarde !== undefined && waarde !== null && waarde !== ''
        ? String(waarde)
        : 'project zonder naam';
    },

    isSelected(id: string): boolean {
      return this.selectedRecords.includes(id);
    },

    isDisabled(id: string): boolean {
      return !this.isSelected(id) && this.selectedRecords.length >= this.maxSelect;
    },

    toggleRow(id: string) {
      if (this.isDisabled(id)) return;
      this.$emit('select', id);
    },

    selectAll() {
      if (this.allSelected) {
        // Deselect all
        this.records.forEach(r => {
          if (this.isSelected(r.id)) {
            this.$emit('select', r.id);
          }
        });
      } else {
        // Select up to maxSelect
        this.records.slice(0, this.maxSelect).forEach(r => {
          if (!this.isSelected(r.id)) {
            this.$emit('select', r.id);
          }
        });
      }
    },

    formatValue(value: any): string {
      if (value === null || value === undefined) return '-';
      if (Array.isArray(value)) return value.join(', ');
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    },

    getFieldLabel(fieldName: string): string {
      return this.fieldLabels[fieldName] || fieldName;
    }
  }
});
</script>

<style scoped>
.compare-table {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.table {
  margin-bottom: 0;
}

.selectable-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.selectable-row:hover:not(.disabled) {
  background-color: rgba(0, 123, 255, 0.1);
}

.selectable-row.selected {
  background-color: rgba(0, 123, 255, 0.15);
  border-left: 4px solid #007bff;
}

.selectable-row.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
}

.empty-state i {
  font-size: 3rem;
  opacity: 0.3;
  margin-bottom: 1rem;
}

.selection-info {
  margin-top: 1rem;
  padding: 0.5rem;
  background: #e7f3ff;
  border-radius: 4px;
  text-align: center;
  font-size: 0.9rem;
  color: #0c5460;
}
</style>









